import { supabase } from './supabase';
import type { FileItem, Folder, Project, Version, UploadTarget } from './types';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function accessToken(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new ApiError(401, 'Your session has expired. Please sign in again.');
  return token;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await accessToken();
  const res = await fetch(path, {
    ...init,
    headers: {
      authorization: `Bearer ${token}`,
      'content-type': 'application/json',
      ...(init.headers as Record<string, string>),
    },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(res.status, body.error ?? `Request failed (${res.status})`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// --- Account ---
export const changePassword = (newPassword: string) =>
  request<{ ok: true }>('/api/account/change-password', {
    method: 'POST',
    body: JSON.stringify({ newPassword }),
  });

// --- Containers ---
export const createFolder = (name: string, parentId?: string) =>
  request<{ folder: Folder }>('/api/folders', {
    method: 'POST',
    body: JSON.stringify({ name, parentId }),
  }).then((r) => r.folder);

export const renameFolder = (id: string, name: string) =>
  request<{ folder: Folder }>(`/api/folders/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  }).then((r) => r.folder);

export const deleteFolder = (id: string) =>
  request<{ ok: true }>(`/api/folders/${id}`, { method: 'DELETE' });

export const createProject = (name: string, opts: { description?: string; folderId?: string } = {}) =>
  request<{ project: Project }>('/api/projects', {
    method: 'POST',
    body: JSON.stringify({ name, ...opts }),
  }).then((r) => r.project);

export const deleteProject = (id: string) =>
  request<{ ok: true }>(`/api/projects/${id}`, { method: 'DELETE' });

export const createVersion = (projectId: string, label: string, notes?: string) =>
  request<{ version: Version }>('/api/versions', {
    method: 'POST',
    body: JSON.stringify({ projectId, label, notes }),
  }).then((r) => r.version);

export const deleteVersion = (id: string) =>
  request<{ ok: true }>(`/api/versions/${id}`, { method: 'DELETE' });

// --- Files ---
export const deleteFile = (id: string) =>
  request<{ ok: true }>(`/api/files/${id}`, { method: 'DELETE' });

export const mintMediaUrl = (fileId: string, mode: 'preview' | 'download') =>
  request<{ url: string }>(`/api/files/${fileId}/media-url`, {
    method: 'POST',
    body: JSON.stringify({ mode }),
  }).then((r) => r.url);

export const createShareLink = (fileId: string) =>
  request<{ shareId: string; token: string; url: string }>(`/api/files/${fileId}/share`, {
    method: 'POST',
  });

export const revokeShareLink = (fileId: string, shareId: string) =>
  request<{ ok: true }>(`/api/files/${fileId}/share/${shareId}/revoke`, { method: 'POST' });

/** Fetch a thumbnail with the bearer header and return an object URL (caller revokes it). */
export async function fetchThumbnailObjectUrl(fileId: string): Promise<string | null> {
  const token = await accessToken();
  const res = await fetch(`/api/files/${fileId}/thumbnail`, {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!res.ok) return null;
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

// --- Uploads ---
export interface UploadInitResult {
  uploadId: string;
  chunkSize: number;
}
export interface UploadProgress {
  status: 'open' | 'completed' | 'aborted';
  bytesReceived: number;
  sizeBytes: number;
  chunkSize: number;
  expiresAt: string;
}

function targetBody(target: UploadTarget, file: File) {
  return {
    targetKind: target.kind,
    targetId: target.kind === 'root' ? undefined : target.id,
    name: file.name,
    mimeType: file.type || 'application/octet-stream',
    sizeBytes: file.size,
  };
}

export const uploadInit = (target: UploadTarget, file: File) =>
  request<UploadInitResult>('/api/uploads/init', {
    method: 'POST',
    body: JSON.stringify(targetBody(target, file)),
  });

export const uploadStatus = (uploadId: string) =>
  request<UploadProgress>(`/api/uploads/${uploadId}`);

export const uploadAbort = (uploadId: string) =>
  request<{ ok: true }>(`/api/uploads/${uploadId}/abort`, { method: 'POST' });

export const uploadFinalize = (uploadId: string) =>
  request<{ done: boolean; file?: FileItem }>(`/api/uploads/${uploadId}/finalize`, { method: 'POST' });

export interface ChunkResponse {
  done: boolean;
  bytesReceived?: number;
  file?: FileItem;
}

/** Send one chunk (raw bytes) with a Content-Range header. */
export async function uploadChunk(
  uploadId: string,
  blob: Blob,
  start: number,
  end: number,
  total: number,
): Promise<ChunkResponse> {
  const token = await accessToken();
  const res = await fetch(`/api/uploads/${uploadId}/chunk`, {
    method: 'PUT',
    headers: {
      authorization: `Bearer ${token}`,
      'content-range': `bytes ${start}-${end}/${total}`,
    },
    body: blob,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(res.status, body.error ?? `Chunk upload failed (${res.status})`);
  }
  return (await res.json()) as ChunkResponse;
}

// --- Admin ---
export interface AdminUser {
  id: string;
  email: string;
  full_name: string;
  role: 'admin' | 'employee';
  is_active: boolean;
  must_change_password: boolean;
  created_at: string;
}

export const adminListUsers = () =>
  request<{ users: AdminUser[] }>('/api/admin/users').then((r) => r.users);

export const adminCreateUser = (email: string, fullName: string, password?: string) =>
  request<{ id: string; email: string; tempPassword: string }>('/api/admin/users', {
    method: 'POST',
    body: JSON.stringify({ email, fullName, password }),
  });

export const adminResetPassword = (id: string, password?: string) =>
  request<{ tempPassword: string }>(`/api/admin/users/${id}/reset-password`, {
    method: 'POST',
    body: JSON.stringify({ password }),
  });

export const adminSetActive = (id: string, active: boolean) =>
  request<{ ok: true }>(`/api/admin/users/${id}/${active ? 'activate' : 'deactivate'}`, {
    method: 'POST',
  });
